import React, { useEffect, useState } from 'react'

import {
  ElementType,
  InnerWidgetPatchBody,
  TWidgetSettingsLoaded,
  WidgetClass,
  WidgetType,
  isCoreWidget
} from 'types/GlobalWidget'
import { KpiOptionObject } from 'types/GlobalKpiOption'
import {
  WidgetTagObject,
  WidgetTagPatchAttributes,
  WidgetTagPostAttributes,
  WidgetTagStatusPatchAttributes,
  WidgetTagStatusPostAttributes
} from 'types/GlobalWidgetTag'

import Badge from 'components_new/atoms/Badge'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Tag from 'components_new/atoms/Tag'
import Tooltip from 'components_new/atoms/Tooltip'

import { AiIconTip } from 'components_new/molecules/IconTip'
import { Option } from 'components_new/molecules/SelectSearch'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'

import ComparativeDisplayTypeSection from 'components_new/molecules/ComparativeDisplayTypeSection'
import DistributionSection from 'components_new/molecules/DistributionSection'
import ShowPerFilterSection from './ShowPerFilterSection'
import BreakdownSection from './BreakdownSection'
import FilterSection from './FilterSection'
import IndicationSection from './IndicationSection'
import MetricSection from './MetricSection'
import RollingSection from 'components_new/molecules/RollingSection'
import TitleSection from './TitleSection'
import UnitSection from 'components_new/molecules/UnitSection'
import ShareKpiSection from './ShareKpiSection'

import StatusSection from './tag/StatusSection'

import EditOrderDialog from './dialogs/EditOrderDialog'
import { useIsCreatingKpiOption } from 'redux/hooks/Widgets'
import { useMetric } from 'redux/hooks/Metrics'
import { TPatchKpiOptionBody, TPostKpiOptionBody } from 'redux/api/KpiOptions'

import { useTranslation } from 'language/translator'

import { getAddTitle, getIconName, getTagOptions } from './utils'
import { getMetricTitle } from 'utils/metric'
import { hasVariantOptions } from 'utils/variantOptions'
import { FINANCIAL_YEAR_PERIOD_FILTERS } from 'utils/constants'

interface DatasetsSectionProps {
  createKpiOption: (
    body: TPostKpiOptionBody,
    callback?: (err?: boolean) => void
  ) => void
  createWidgetTag: (body: WidgetTagPostAttributes) => void
  createWidgetTagStatus: (
    body: WidgetTagStatusPostAttributes,
    tagId: string
  ) => void
  deleteKpiOption: (id: string) => void
  deleteWidgetTag: (id: string) => void
  deleteWidgetTagStatus: (id: string, tagId: string) => void
  id: string
  onClose: () => void
  widget: TWidgetSettingsLoaded
  updateKpiOption: (
    id: string,
    body: TPatchKpiOptionBody,
    cb: () => void
  ) => void
  updateWidget: (body: InnerWidgetPatchBody) => void
  updateWidgetTag: (id: string, body: WidgetTagPatchAttributes) => void
  updateWidgetTagStatus: (
    id: string,
    body: WidgetTagStatusPatchAttributes,
    tagId: string
  ) => void
}

type StandardizedElement = {
  id: string
  ai_generated: boolean
  tag_option_id: string | null
  type: ElementType
  title: string
  index: number
  hidden: boolean
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.BUBBLE_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.KEY_FIGURE,
  WidgetType.LINE_CHART,
  WidgetType.MAP,
  WidgetType.LIST,
  WidgetType.PIE_CHART,
  WidgetType.FUNNEL
]

const DatasetsSection = (props: DatasetsSectionProps) => {
  const {
    createKpiOption,
    createWidgetTag,
    createWidgetTagStatus,
    deleteKpiOption,
    deleteWidgetTag,
    deleteWidgetTagStatus,
    id,
    onClose,
    widget,
    updateKpiOption,
    updateWidget,
    updateWidgetTag,
    updateWidgetTagStatus
  } = props
  const { t, lang, translateEnum } = useTranslation()
  const isCreatingKpiOption = useIsCreatingKpiOption()

  const [addKpiDialogOpen, setAddKpiDialogOpen] = useState<boolean>(false)
  const [editOrderDialogOpen, setEditOrderDialogOpen] = useState<boolean>(false)

  const [dialog, setDialog] = useState<{
    open: boolean
    property: TProperty
    title: string
    type: 'add' | 'edit'
    data: {
      options: Option[]
      value: string
      type: TInputType
    }
  }>({
    open: false,
    property: 'tags',
    title: '',
    type: 'edit',
    data: {
      options: [],
      value: '',
      type: 'text'
    }
  })

  /*-- merge kpis and tags --*/
  const [tempElements, setTempElements] = useState<StandardizedElement[]>([])

  useEffect(() => {
    const kpiElements = widget.settings.kpi_options
      .filter((kpi) => kpi.index !== 0) // Don't include the main KPI.
      .map((kpi) => ({
        id: kpi.id,
        ai_generated: false,
        tag_option_id: null,
        type: ElementType.KPI,
        title: kpi.title,
        index: kpi.index,
        hidden: kpi.hidden
      }))

    const tagElements = widget.settings.tags.selected.map((tag) => {
      return {
        id: tag.id,
        ai_generated: !!widget.settings.tags.options.find(
          (opt) => opt.ai_generated && opt.id === tag.tag_option_id
        ),
        tag_option_id: tag.tag_option_id,
        type: ElementType.TAG,
        title: tag.title,
        index: tag.index,
        hidden: false
      }
    })

    const elems: StandardizedElement[] = [...kpiElements, ...tagElements]

    elems.sort((a, b) => a.index - b.index)

    setTempElements(elems)
  }, [widget.settings.kpi_options, widget.settings.tags.selected])

  /*-- drawer --*/
  const [drawer, setDrawer] = useState<{
    open: boolean
    title: string
    type: ElementType
    data: {
      kpi: KpiOptionObject | null
      tag: WidgetTagObject | null
    }
  }>({
    open: false,
    title: '',
    type: ElementType.KPI,
    data: { kpi: null, tag: null }
  })

  // handleUpdateDrawer for kpi
  useEffect(() => {
    if (drawer.open && drawer.data.kpi) {
      handleOpenDrawer(drawer.data.kpi.id, drawer.type)
    }
  }, [widget.settings.kpi_options])

  // handleUpdateDrawer for tag
  useEffect(() => {
    if (drawer.open && drawer.data.tag) {
      handleOpenDrawer(drawer.data.tag.id, drawer.type)
    }
  }, [widget.settings.tags])

  /*-- main kpi --*/
  // a financial year period rules out rolling sums and some
  // distributions
  const isFinancialYear = FINANCIAL_YEAR_PERIOD_FILTERS.includes(
    widget.settings.date_filter.selected.value
  )

  // the variant options carry these settings per column, so the
  // metric's own are not used
  const variantOptionsActive = hasVariantOptions(widget.settings)
  const variantOptionSetting = t(
    'widgets',
    'setting_controlled_by_variant_option'
  )

  const mainKpi = widget.settings.kpi_options.find(
    (kpi) => kpi.index === 0
  ) as KpiOptionObject

  const handleBackDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, open: false }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleOpenDrawer = (id: string, type: ElementType) => {
    let kpi = null
    let tag = null
    let title = ''

    if (type === ElementType.KPI) {
      kpi = widget.settings.kpi_options.find((kpi) => kpi.id === id)
      title = kpi?.title ?? ''
    } else {
      tag = widget.settings.tags.selected.find((tag) => tag.id === id)
      title = tag?.title ?? ''
    }

    setDrawer({
      open: true,
      title: title,
      type: type,
      data: { kpi: kpi ?? null, tag: tag ?? null }
    })
  }

  const editedKpi = drawer.data.kpi

  // the metric behind the edited dataset, which carries the rolling
  // and distribution options of the selected variant
  const editedMetric = useMetric(
    editedKpi?.kpi_template_id,
    editedKpi?.variant_kpi_template_id
  )
  const editedTag = drawer.data.tag

  // Properties edited in this component.
  type TProperty = 'tags' | 'title'

  /*-- dialogs --*/
  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (property: TProperty) => {
    let dialogTitle = ''
    let dialogType: 'add' | 'edit' = 'edit'
    let inputValue = ''
    let inputType: TInputType = 'text'
    let inputOptions: Option[] = []

    switch (property) {
    case 'title':
      dialogTitle = t('global', 'name')
      inputType = 'text'
      inputValue = editedTag?.title ?? ''
      break
    case 'tags':
      dialogTitle = t('widgets', 'attribute')
      dialogType = 'add'
      inputOptions = getTagOptions(widget.settings.tags, t)
      inputType = 'select'
      inputValue = ''
      break
    }

    setDialog({
      open: true,
      property: property,
      title: dialogTitle,
      type: dialogType,
      data: {
        options: inputOptions,
        type: inputType,
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    switch (dialog.property) {
    case 'title':
      if (editedTag) {
        updateWidgetTag(editedTag.id, {
          data: { title: dialog.data.value }
        })
      }
      break
    case 'tags':
      createWidgetTag({
        data: {
          widget_id: id,
          tag_option_id: dialog.data.value
        }
      })
      break
    }
  }

  if (!LEGAL_TYPES.includes(widget.settings.type.selected)) {
    return null
  }

  const type = widget.settings.type.selected
  const numberOfKpis = widget.settings.kpi_options.length

  // A horizontal table always shows its metrics as rows. A segment
  // hierarchy additionally makes those rows expandable. With variant
  // columns the columns are comparison figures, so the text does not
  // apply.
  const metricRowsExpandable =
    type === WidgetType.LIST &&
    widget.settings.horizontal &&
    widget.settings.segment_by.additional.length > 0 &&
    widget.settings.variant_options.length === 0

  const showAdd =
    type === WidgetType.BAR_CHART ||
    type === WidgetType.LINE_CHART ||
    type === WidgetType.LIST ||
    type === WidgetType.KEY_FIGURE ||
    (type === WidgetType.BUBBLE_CHART && numberOfKpis <= 2) ||
    (type === WidgetType.COMBO_CHART && numberOfKpis <= 1) ||
    (type === WidgetType.MAP && numberOfKpis <= 9)

  return (
    <>
      <SettingsGroup
        title="Data"
        helperText={
          metricRowsExpandable
            ? t('widgets', 'expandable_metric_rows_helper')
            : undefined
        }
        actions={
          type === WidgetType.LIST ||
          type === WidgetType.KEY_FIGURE ||
          type === WidgetType.BAR_CHART ||
          type === WidgetType.LINE_CHART ||
          type === WidgetType.BUBBLE_CHART ? (
                <Link onClick={() => setEditOrderDialogOpen(true)} variant="body2">
                  <Icon
                    color="inherit"
                    fontSize="inherit"
                    name={'Edit'}
                    sx={{
                      mr: '0.25em'
                    }}
                  />
                  {t('global', 'edit_order')}
                </Link>
              ) : undefined
        }
      >
        <SettingsItem
          icon={
            <Tooltip title={t('widgets', 'primary_metric_tooltip')}>
              <Badge color="success" variant="dot">
                <Icon name={getIconName(mainKpi.index, widget.settings)} />
              </Badge>
            </Tooltip>
          }
          onClick={() => handleOpenDrawer(mainKpi.id, ElementType.KPI)}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {mainKpi.title}
              {widget.settings.type.selected === WidgetType.LIST ? (
                <>
                  <Tooltip placement={'top'} title={t('widgets', 'metric')}>
                    <Box>
                      <Tag
                        startIconName={'TrendingUpOutlined'}
                        color="primary"
                      />
                    </Box>
                  </Tooltip>
                  {mainKpi.hidden ? (
                    <Tooltip
                      placement={'top'}
                      title={t('widgets', 'column_is_hidden')}
                    >
                      <Box>
                        <Tag
                          startIconName={'VisibilityOffOutlined'}
                          color="primary"
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                </>
              ) : null}
            </Box>
          }
        />
        {tempElements.map((item) => (
          <>
            <SettingsItem
              icon={<Icon name={getIconName(item.index, widget.settings)} />}
              onClick={() => handleOpenDrawer(item.id, item.type)}
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.title}
                  {item.ai_generated ? (
                    <AiIconTip title={t('global', 'ai_generated')} />
                  ) : null}
                  {widget.settings.type.selected === WidgetType.LIST ? (
                    <>
                      <Tooltip
                        placement={'top'}
                        title={
                          item.type === ElementType.KPI
                            ? t('widgets', 'metric')
                            : t('widgets', 'attribute')
                        }
                      >
                        <Box>
                          <Tag
                            startIconName={
                              item.type === ElementType.KPI
                                ? 'TrendingUpOutlined'
                                : 'SellOutlined'
                            }
                            color="primary"
                          />
                        </Box>
                      </Tooltip>
                      {item.hidden ? (
                        <Tooltip
                          placement={'top'}
                          title={t('widgets', 'column_is_hidden')}
                        >
                          <Box>
                            <Tag
                              startIconName={'VisibilityOffOutlined'}
                              color="primary"
                            />
                          </Box>
                        </Tooltip>
                      ) : null}
                    </>
                  ) : null}
                </Box>
              }
            />
          </>
        ))}
        {showAdd ? (
          <SettingsItem
            color="success"
            onClick={() => setAddKpiDialogOpen(true)}
            title={`${getAddTitle(widget.settings, numberOfKpis, t)}`}
            variant="add"
          />
        ) : null}
        {widget.settings.type.selected === WidgetType.LIST ? (
          <SettingsItem
            color="success"
            disabled={
              variantOptionsActive || widget.settings.tags.options.length === 0
            }
            disabledText={
              variantOptionsActive
                ? t('widgets', 'cannot_add_attribute_with_variant_options')
                : t('widgets', 'no_attributes_available_for_segmentation')
            }
            onClick={() => handleOpenDialog('tags')}
            title={t('widgets', 'add_attribute')}
            variant="add"
          />
        ) : null}
      </SettingsGroup>

      {/*-- drawer --*/}
      {/* if kpi */}
      <SettingsDrawer
        open={drawer.open && drawer.type === ElementType.KPI}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={drawer.title}
      >
        {editedKpi ? (
          <>
            <TitleSection
              kpi={editedKpi}
              settings={widget.settings}
              updateKpiOption={(data) =>
                updateKpiOption(editedKpi.id, data, () => {})
              }
            />
            <MetricSection
              kpi={editedKpi}
              onClose={onClose}
              updateKpiOption={(data, cb) =>
                updateKpiOption(editedKpi.id, data, cb)
              }
            />
            <UnitSection
              autoFormat={editedKpi.auto_format}
              decimalsDisabledText={
                variantOptionsActive ? variantOptionSetting : undefined
              }
              equivalent={editedKpi.equivalent}
              numberOfDecimals={editedKpi.number_of_decimals}
              onClose={onClose}
              onUpdate={(data) => updateKpiOption(editedKpi.id, data, () => {})}
              unit={editedKpi.unit}
              unitPrefix={editedKpi.unit_prefix}
            />
            <RollingSection
              disabled={variantOptionsActive}
              disabledText={variantOptionSetting}
              isFinancialYear={isFinancialYear}
              onChange={(rolling_sum) =>
                updateKpiOption(editedKpi.id, { rolling_sum }, () => {})
              }
              options={editedMetric?.rolling_sums ?? []}
              selected={editedKpi.rolling_sum}
            />
            {editedMetric && !editedKpi.is_share_kpi ? (
              <DistributionSection
                disabled={variantOptionsActive}
                disabledText={variantOptionSetting}
                distributions={editedMetric.distributions}
                filters={{
                  count: editedKpi.show_per_filters.filter(
                    (f) => f.attribute_option.selected
                  ).length,
                  content: (
                    <ShowPerFilterSection
                      id={id}
                      kpi={editedKpi}
                      onClose={handleCloseDrawer}
                      settings={widget.settings}
                      showPerFilterOptions={
                        editedMetric.distributions.find(
                          (opt) => opt.enum === editedKpi.distribution
                        )?.filter_options || []
                      }
                      updateKpiOption={(data) =>
                        updateKpiOption(editedKpi.id, data, () => {})
                      }
                    />
                  )
                }}
                isFinancialYear={isFinancialYear}
                onToggle={(distribution) =>
                  updateKpiOption(editedKpi.id, { distribution }, () => {})
                }
                selected={editedKpi.distribution}
                subject={editedKpi.title}
              />
            ) : null}
            <ComparativeDisplayTypeSection
              disabled={variantOptionsActive}
              disabledText={variantOptionSetting}
              hasComparativePeriod={
                !!widget.settings.comparative_period.selected?.value
              }
              onChange={(comparative_display_type) =>
                updateKpiOption(
                  editedKpi.id,
                  { comparative_display_type },
                  () => {}
                )
              }
              options={editedKpi.comparative_display_type.options}
              selected={editedKpi.comparative_display_type.selected}
            />
            <FilterSection
              kpi={editedKpi}
              onClose={handleCloseDrawer}
              settings={widget.settings}
              updateKpiOption={(data) =>
                updateKpiOption(editedKpi.id, data, () => {})
              }
            />
            <BreakdownSection
              widgetClass={widget.class}
              kpi={editedKpi}
              settings={widget.settings}
              updateKpiOption={(data) =>
                updateKpiOption(editedKpi.id, data, () => {})
              }
            />
            <IndicationSection
              kpi={editedKpi}
              onClose={handleCloseDrawer}
              settings={widget.settings}
              updateKpiOption={(data) =>
                updateKpiOption(editedKpi.id, data, () => {})
              }
            />
            <ShareKpiSection
              id={id}
              kpi={editedKpi}
              onClose={handleCloseDrawer}
              settings={widget.settings}
              updateKpiOption={(data) =>
                updateKpiOption(editedKpi.id, data, () => {})
              }
            />
            <SettingsGroup
              helperText={
                editedKpi.index === 0
                  ? t('widgets', 'cannot_delete_primary_metric')
                  : undefined
              }
            >
              <SettingsItem
                color="error"
                disabled={editedKpi.index === 0}
                onClick={() => {
                  deleteKpiOption(editedKpi.id)
                  handleBackDrawer()
                }}
                title={t('global', 'delete')}
                variant="action"
              />
            </SettingsGroup>
          </>
        ) : null}
      </SettingsDrawer>

      {/* if tag */}
      <SettingsDrawer
        open={drawer.open && drawer.type === ElementType.TAG}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={drawer.title}
      >
        {editedTag ? (
          <>
            <SettingsGroup>
              <SettingsItem
                onClick={() => handleOpenDialog('title')}
                title={t('global', 'name')}
                value={editedTag.title}
                variant="edit"
              />
            </SettingsGroup>

            <StatusSection
              createWidgetTagStatus={createWidgetTagStatus}
              deleteWidgetTagStatus={deleteWidgetTagStatus}
              onClose={handleCloseDrawer}
              tag={editedTag}
              updateWidgetTagStatus={updateWidgetTagStatus}
              widgetId={id}
            />

            <SettingsGroup>
              <SettingsItem
                color="error"
                onClick={() => {
                  deleteWidgetTag(editedTag.id)
                  handleBackDrawer()
                }}
                title={t('global', 'delete')}
                variant="action"
              />
            </SettingsGroup>
          </>
        ) : null}
      </SettingsDrawer>

      {/*-- dialogs --*/}
      <MetricsDialog
        availableSteps={['variant', 'slices']}
        creating={isCreatingKpiOption}
        handleOnAdd={(metric, settings) => {
          const title = getMetricTitle(metric, settings, lang, translateEnum)

          createKpiOption(
            {
              widget_id: id,
              kpi_template_id: metric.id,
              ...settings,
              title
            },
            (err) => {
              if (!err) setAddKpiDialogOpen(false)
            }
          )
        }}
        open={addKpiDialogOpen}
        onClose={() => setAddKpiDialogOpen(false)}
        sx={{ zIndex: 1302 }}
        title={t('metrics', 'add_metric')}
        variant={widget.class === WidgetClass.REPORT ? 'reports' : 'dashboards'}
        widget={isCoreWidget(widget) ? widget : undefined}
      />

      <EditOrderDialog
        onClose={() => setEditOrderDialogOpen(false)}
        open={editOrderDialogOpen}
        updateWidget={updateWidget}
        widgetId={id}
        widgetSettings={widget.settings}
      />

      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        required={dialog.property === 'title'}
        title={dialog.title}
        type={dialog.data.type}
        value={dialog.data.value}
        variant={dialog.type}
      />
    </>
  )
}

export default DatasetsSection
